import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  adminCreateRole,
  adminDeleteRole,
  adminGetRoles,
  adminGetRolesForDropdown,
  adminUpdateRole,
  adminVerifyRoleDelete,
} from './adminApi';

export const ROLE_NAMES = {
  ADMIN: 'Admin',
  APPROVER: 'Approver',
  ENTERER: 'Enterer',
  STUDENT: 'Student',
  TUTOR: 'Tutor',
  GUEST: 'Guest',
} as const;

export const ROLE_TYPES = {
  ADMIN: 'admin',
  USER: 'user',
} as const;

export type TRoleNames = typeof ROLE_NAMES[keyof typeof ROLE_NAMES];
export type TRoleTypes = typeof ROLE_TYPES[keyof typeof ROLE_TYPES];

export const SUBMISSION_PERMISSIONS = {
  SUBMISSION_VIEW: 'view',
  SUBMISSION_CREATE: 'create',
  SUBMISSION_EDIT: 'edit',
  SUBMISSION_DELETE: 'delete',
  SUBMISSION_AI_ANSWER: 'aiAnswer',
  SUBMISSION_MANUAL_ANSWER: 'manualAnswer',
} as const;

const COURSE_PERMISSIONS = {
  COURSE_VIEW: 'view',
  COURSE_CHAT: 'courseChat',
  COURSE_CALL: 'courseCall',
  COURSE_CLASS_BOOKING: 'classBooking',
  COURSE_STUDENT_NAME: 'studentName',
  COURSE_TUTOR_NAME: 'tutorName',
  COURSE_TOPUP: 'courseTopup',
  // Admin
  ADMIN_COURSE_VIEW: 'view',
  ADMIN_COURSE_CREATE: 'create',
  ADMIN_COURSE_EDIT: 'edit',
  ADMIN_COURSE_DELETE: 'delete',
  ADMIN_COURSE_PUBLISH: 'publish',
  ADMIN_COURSE_CHAT: 'courseChat',
  ADMIN_COURSE_DELETE_WITH_CREDITS: 'deleteWithCredits',
} as const;

const CLASS_PERMISSIONS = {
  CLASS_VIEW: 'view',
  CLASS_CHAT: 'classChat',
  // Admin
  ADMIN_CLASS_VIEW: 'view',
  ADMIN_CLASS_CREATE: 'create',
  ADMIN_CLASS_EDIT: 'edit',
  ADMIN_CLASS_DELETE: 'delete',
  ADMIN_CLASS_PUBLISH: 'publish',
  ADMIN_CLASS_CHAT: 'classChat',
} as const;

const COURSE_TEMPLATE_PERMISSIONS = {
  // Admin
  ADMIN_COURSE_TEMPLATE_CREATE: 'create',
  ADMIN_COURSE_TEMPLATE_VIEW: 'view',
  ADMIN_COURSE_TEMPLATE_EDIT: 'edit',
  ADMIN_COURSE_TEMPLATE_DELETE: 'delete',
} as const;

const SCHEDULE_PERMISSIONS = {
  SCHEDULE_VIEW: 'view',
  SCHEDULE_EDIT: 'edit',
  // Admin
  ADMIN_SCHEDULE_CREATE: 'create',
  ADMIN_SCHEDULE_VIEW: 'view',
  ADMIN_SCHEDULE_EDIT: 'edit',
} as const;

const LESSON_HISTORY_PERMISSIONS = {
  LESSON_HISTORY_VIEW: 'view',
  // Admin
  ADMIN_LESSON_HISTORY_VIEW: 'view',
} as const;

const LESSON_HISTORY_TABS_PERMISSIONS = {
  LESSON_HISTORY_COMPLETED_TAB: 'completedTab',
  LESSON_HISTORY_RESCHEDULE_OR_CANCELLATION_TAB: 'rescheduleOrCancellationTab',
  // Admin
  ADMIN_LESSON_HISTORY_COMPLETED_TAB: 'completedTab',
  ADMIN_LESSON_HISTORY_RESCHEDULE_OR_CANCELLATION_TAB:
    'rescheduleOrCancellationTab',
} as const;

const USER_TYPE_PERMISSIONS = {
  STUDENT: 'student',
  TUTOR: 'tutor',
  ADMIN: 'admin',
  GUEST: 'guest',
} as const;

const LESSON_HISTORY_COMPLETED_TAB_PERMISSIONS = {
  LESSON_HISTORY_COMPLETED_TAB_REFERENCE_KEY: 'referenceKey',
  LESSON_HISTORY_COMPLETED_TAB_DATE_TIME: 'dateTime',
  LESSON_HISTORY_COMPLETED_TAB_STUDENTS: 'students',
  LESSON_HISTORY_COMPLETED_TAB_TUTORS: 'tutors',
  LESSON_HISTORY_COMPLETED_TAB_RECORDINGS: 'recordings',
  LESSON_HISTORY_COMPLETED_TAB_ACTIONS: 'actions',
  LESSON_HISTORY_COMPLETED_TAB_CURRICULUM: 'curriculum',
  LESSON_HISTORY_COMPLETED_TAB_SUBJECT: 'subject',
  LESSON_HISTORY_COMPLETED_TAB_GRADE: 'grade',
  LESSON_HISTORY_COMPLETED_TAB_VIEW_FEEDBACKS: 'viewFeedbacks',
  LESSON_HISTORY_COMPLETED_TAB_LESSON_TYPE: 'lessonType',
  LESSON_HISTORY_COMPLETED_TAB_PAYMENT_TYPE: 'paymentType',
  LESSON_HISTORY_COMPLETED_TAB_STUDENT_JOIN_TIME: 'studentJoinTime',
  LESSON_HISTORY_COMPLETED_TAB_TUTOR_JOIN_TIME: 'tutorJoinTime',
  LESSON_HISTORY_COMPLETED_TAB_TUTOR_RATE: 'tutorRate',
  LESSON_HISTORY_COMPLETED_TAB_TUTOR_AMOUNT: 'tutorAmount',
  LESSON_HISTORY_COMPLETED_TAB_TUTOR_PENALTY: 'tutorPenalty',
  LESSON_HISTORY_COMPLETED_TAB_TUTOR_PAYMENT_BREAKDOWN: 'tutorPaymentBreakdown',
  LESSON_HISTORY_COMPLETED_TAB_TRIAL_STATUS: 'trialStatus',
  // Admin
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_REFERENCE_KEY: 'referenceKey',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_DATE_TIME: 'dateTime',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_STUDENTS: 'students',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTORS: 'tutors',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_RECORDINGS: 'recordings',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_ACTIONS: 'actions',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_CURRICULUM: 'curriculum',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_SUBJECT: 'subject',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_GRADE: 'grade',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_ATTENDANCE_HISTORY: 'attendanceHistory',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_ADMIN_NOTES: 'adminNotes',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_VIEW_FEEDBACKS: 'viewFeedbacks',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_LESSON_TYPE: 'lessonType',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_TYPE: 'paymentType',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_STUDENT_COUNTRIES: 'studentCountries',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_STUDENT_JOIN_TIME: 'studentJoinTime',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_JOIN_TIME: 'tutorJoinTime',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_RATE: 'tutorRate',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_AMOUNT: 'tutorAmount',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_PAYMENT_STATUS: 'tutorPaymentStatus',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_PENALTY: 'tutorPenalty',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_ADJUSTMENTS:
    'paymentAdjustmentsAdjustments',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_CLASS_EXTENSION:
    'paymentAdjustmentsClassExtension',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_PAYMENT_HISTORY:
    'tutorPaymentHistory',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_TUTOR_PENALTY:
    'paymentAdjustmentsTutorPenalty',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_TRIAL_CONVERSION:
    'paymentAdjustmentsTrialConversion',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_PENALTY_WAIVERS: 'penaltyWaivers',
  ADMIN_LESSON_HISTORY_COMPLETED_TAB_TRIAL_STATUS: 'trialStatus',
} as const;

const PROFILE_PERMISSIONS = {
  PROFILE_VIEW: 'view',
  PROFILE_EDIT: 'edit',
  PROFILE_DELETE: 'delete',
  PROFILE_QUALIFICATIONS: 'qualifications',
  PROFILE_SUBJECTS: 'subjects',
  PROFILE_PARENT: 'parentDetails',
  PROFILE_PLANS: 'plans',
  PROFILE_PAYMENT_HISTORY: 'paymentHistory',
  PROFILE_BILLING_EMAIL: 'billingEmail',
  PROFILE_TUTOR_RATES: 'tutorRates',
  PROFILE_DATE_OF_BIRTH: 'dateOfBirth',
  PROFILE_NATIONALITY: 'nationality',
  PROFILE_EMERGENCY_NUMBER: 'emergencyNumber',
  PROFILE_TUTOR: 'tutorDetails',
  PROFILE_BANK: 'bankDetails',
  PROFILE_CURRICULUM: 'curriculum',
  PROFILE_EXAM_DATE: 'examDate',
  PROFILE_ADMIN_TAG: 'adminTag',
  PROFILE_CLASS_STATEMENT: 'classStatement',
  PROFILE_MS_TEAMS_EMAIL: 'msTeamsEmail',
  // ADMIN
  ADMIN_USER_PROFILE_ADMIN_TAG: 'adminTag',
} as const;

const TRIAL_CLASS_PERMISSIONS = {
  TRIAL_CLASS_VIEW: 'view',
  TRIAL_CLASS_CREATE: 'create',
  // Admin
  ADMIN_TRIAL_CLASS_VIEW: 'view',
  ADMIN_TRIAL_CLASS_CREATE: 'create',
  ADMIN_TRIAL_CLASS_EDIT: 'edit',
} as const;

const HOMEWORK_PERMISSIONS = {
  HOMEWORK_VIEW: 'view',
  HOMEWORK_CREATE: 'create',
  HOMEWORK_EDIT: 'edit',
  HOMEWORK_DELETE: 'delete',
  // Admin
  ADMIN_HOMEWORK_VIEW: 'view',
  ADMIN_HOMEWORK_CREATE: 'create',
  ADMIN_HOMEWORK_EDIT: 'edit',
  ADMIN_HOMEWORK_DELETE: 'delete',
} as const;

const BILLING_PERMISSIONS = {
  BILLING_VIEW: 'view',
  CUSTOMER_PORTAL_VIEW: 'customerPortalView',
} as const;

const USERS_PERMISSIONS = {
  // Admin
  ADMIN_USERS_CREATE: 'create',
  ADMIN_USERS_VIEW: 'view',
  ADMIN_USERS_EDIT: 'edit',
} as const;

const ROLES_PERMISSIONS = {
  // Admin
  ADMIN_ROLES_VIEW: 'view',
} as const;

const HEADER_CONTENTS_PERMISSIONS = {
  HEADER_CONTENTS_PROFILE_DROPDOWN: 'profileDropDown',
  HEADER_CONTENTS_SIGNUP_BUTTONS: 'signUpButtons',
  HEADER_CONTENTS_DISCOUNT_BANNER: 'discountBanner',
  HEADER_CONTENTS_DASHBOARD: 'dashboard',
  HEADER_CONTENTS_CREDITS_VIEWER: 'creditsViewer',
  HEADER_CONTENTS_UPGRADE_BUTTON: 'upgradeButton',
} as const;

const ADMIN_SETTINGS_PERMISSIONS = {
  // Admin
  ADMIN_SETTINGS_VIEW: 'view',

  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_CREATE:
    'coursePermissionTemplatesCreate',
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_VIEW:
    'coursePermissionTemplatesView',
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_EDIT:
    'coursePermissionTemplatesEdit',
  ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_DELETE:
    'coursePermissionTemplatesDelete',

  ADMIN_SETTINGS_FLEXIBLE_PLANS_CREATE: 'flexiblePlansCreate',
  ADMIN_SETTINGS_FLEXIBLE_PLANS_VIEW: 'flexiblePlansView',
  ADMIN_SETTINGS_FLEXIBLE_PLANS_DELETE: 'flexiblePlansDelete',
  ADMIN_SETTINGS_FLEXIBLE_PLANS_EDIT: 'flexiblePlansEdit',

  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_CREATE: 'subscriptionPlansCreate',
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_VIEW: 'subscriptionPlansView',
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_EDIT: 'subscriptionPlansEdit',
  ADMIN_SETTINGS_SUBSCRIPTION_PLANS_DELETE: 'subscriptionPlansDelete',

  ADMIN_SETTINGS_GROUPS_VIEW: 'groupsView',
  ADMIN_SETTINGS_GROUPS_CREATE: 'groupsCreate',
  ADMIN_SETTINGS_GROUPS_EDIT: 'groupsEdit',
  ADMIN_SETTINGS_GROUPS_DELETE: 'groupsDelete',

  ADMIN_SETTINGS_STUDENT_RATES_VIEW: 'studentRatesView',
  ADMIN_SETTINGS_STUDENT_RATES_CREATE: 'studentRatesCreate',
  ADMIN_SETTINGS_STUDENT_RATES_EDIT: 'studentRatesEdit',
  ADMIN_SETTINGS_STUDENT_RATES_DELETE: 'studentRatesDelete',

  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_VIEW: 'coursePricingTemplatesView',
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_CREATE:
    'coursePricingTemplatesCreate',
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_EDIT: 'coursePricingTemplatesEdit',
  ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_DELETE:
    'coursePricingTemplatesDelete',

  ADMIN_SETTINGS_ROLES_VIEW: 'rolesView',
  ADMIN_SETTINGS_ROLES_CREATE: 'rolesCreate',
  ADMIN_SETTINGS_ROLES_EDIT: 'rolesEdit',
  ADMIN_SETTINGS_ROLES_DELETE: 'rolesDelete',

  ADMIN_SETTINGS_TUTOR_ROLES_VIEW: 'tutorRolesView',
  ADMIN_SETTINGS_TUTOR_ROLES_CREATE: 'tutorRolesCreate',
  ADMIN_SETTINGS_TUTOR_ROLES_EDIT: 'tutorRolesEdit',
  ADMIN_SETTINGS_TUTOR_ROLES_DELETE: 'tutorRolesDelete',

  ADMIN_SETTINGS_INVOICES_VIEW: 'invoicesView',
  ADMIN_SETTINGS_INVOICES_CREATE: 'invoicesCreate',
  ADMIN_SETTINGS_INVOICES_EDIT: 'invoicesEdit',

  ADMIN_SETTINGS_TUTOR_RATES_VIEW: 'tutorRatesView',
  ADMIN_SETTINGS_TUTOR_RATES_CREATE: 'tutorRatesCreate',
  ADMIN_SETTINGS_TUTOR_RATES_EDIT: 'tutorRatesEdit',
  ADMIN_SETTINGS_TUTOR_RATES_DELETE: 'tutorRatesDelete',

  ADMIN_SETTINGS_META_TAGS_VIEW: 'metaTagsView',
  ADMIN_SETTINGS_META_TAGS_CREATE: 'metaTagsCreate',
  ADMIN_SETTINGS_META_TAGS_EDIT: 'metaTagsEdit',
  ADMIN_SETTINGS_META_TAGS_DELETE: 'metaTagsDelete',

  ADMIN_SETTINGS_REDIRECTIONS_VIEW: 'redirectionsView',
  ADMIN_SETTINGS_REDIRECTIONS_CREATE: 'redirectionsCreate',
  ADMIN_SETTINGS_REDIRECTIONS_EDIT: 'redirectionsEdit',
  ADMIN_SETTINGS_REDIRECTIONS_DELETE: 'redirectionsDelete',

  ADMIN_SETTINGS_SITEMAP_VIEW: 'sitemapView',
  ADMIN_SETTINGS_SITEMAP_CREATE: 'sitemapCreate',

  ADMIN_SETTINGS_ADJUSTMENTS_CREATE: 'adjustmentsCreate',

  ADMIN_SETTINGS_PERMISSIONS_VIEW: 'permissionsView',

  ADMIN_SETTINGS_TRANSACTION_LOGS_VIEW: 'transactionLogsView',

  ADMIN_SETTINGS_PAYMENT_HISTORY_VIEW: 'paymentHistoryView',

  ADMIN_SETTINGS_TUTOR_SORTING_VIEW: 'tutorSortingView',
  ADMIN_SETTINGS_TUTOR_SORTING_EDIT: 'tutorSortingEdit',

  ADMIN_SETTINGS_CALL_LOGS_VIEW: 'callLogsView',
} as const;

const PENALTY_WAIVERS_PERMISSIONS = {
  PENALTY_WAIVERS_VIEW: 'view',
  PENALTY_APPEAL_REQUEST: 'appealRequest',
  // Admin
  ADMIN_PENALTY_WAIVERS_VIEW: 'view',
  ADMIN_PENALTY_APPEAL_REQUEST: 'appealRequest',
} as const;

const MANUAL_ADJUSTMENTS = {
  // Admin
  ADMIN_MANUAL_ADJUSTMENTS_VIEW: 'view',
  ADMIN_MANUAL_ADJUSTMENTS_CREATE: 'create',
} as const;

const QUIZ_PERFORMANCE_PERMISSIONS = {
  QUIZ_PERFORMANCE_VIEW: 'view',
} as const;

const MY_PERFORMANCE_PERMISSIONS = {
  MY_PERFORMANCE_VIEW: 'view',
} as const;

const TUTORS_PERMISSIONS = {
  TUTORS_HIDE: 'hide',
} as const;

export const META_TAGS_PERMISSIONS = {
  META_TAGS_VIEW: 'view',
} as const;

export const RESOURCES_PERMISSIONS = {
  RESOURCES_WHITEBOARD: 'whiteboard',
  RESOURCES_SESSIONS: 'sessions',
  RESOURCES_BOOKMARKS: 'bookmarks',
  RESOURCES_PROGRESS: 'progress',
  RESOURCES_PREMIUM: 'premium',
  RESOURCES_USAGE: 'usage',
} as const;

export const QUIZ_BUILDER_PERMISSIONS = {
  QUIZ_BUILDER_VIEW: 'view',
  QUIZ_BUILDER_CREATE: 'create',
  QUIZ_BUILDER_EDIT: 'edit',
  QUIZ_BUILDER_DELETE: 'delete',
} as const;

export const USER_PERMISSION_INDIVIDUALS = {
  ...SUBMISSION_PERMISSIONS,
  ...COURSE_PERMISSIONS,
  ...CLASS_PERMISSIONS,
  ...COURSE_TEMPLATE_PERMISSIONS,
  ...SCHEDULE_PERMISSIONS,
  ...LESSON_HISTORY_PERMISSIONS,
  ...LESSON_HISTORY_TABS_PERMISSIONS,
  ...LESSON_HISTORY_COMPLETED_TAB_PERMISSIONS,
  ...PROFILE_PERMISSIONS,
  ...TRIAL_CLASS_PERMISSIONS,
  ...HOMEWORK_PERMISSIONS,
  ...BILLING_PERMISSIONS,
  ...USERS_PERMISSIONS,
  ...ROLES_PERMISSIONS,
  ...ADMIN_SETTINGS_PERMISSIONS,
  ...HEADER_CONTENTS_PERMISSIONS,
  ...PENALTY_WAIVERS_PERMISSIONS,
  ...MANUAL_ADJUSTMENTS,
  ...QUIZ_PERFORMANCE_PERMISSIONS,
  ...MY_PERFORMANCE_PERMISSIONS,
  ...TUTORS_PERMISSIONS,
  ...USER_TYPE_PERMISSIONS,
  ...META_TAGS_PERMISSIONS,
  ...RESOURCES_PERMISSIONS,
  ...QUIZ_BUILDER_PERMISSIONS,
} as const;

export const USER_PERMISSION = {
  // SUBMISSION
  SUBMISSION: [
    USER_PERMISSION_INDIVIDUALS.SUBMISSION_VIEW,
    USER_PERMISSION_INDIVIDUALS.SUBMISSION_CREATE,
    USER_PERMISSION_INDIVIDUALS.SUBMISSION_EDIT,
    USER_PERMISSION_INDIVIDUALS.SUBMISSION_DELETE,
    USER_PERMISSION_INDIVIDUALS.SUBMISSION_AI_ANSWER,
    USER_PERMISSION_INDIVIDUALS.SUBMISSION_MANUAL_ANSWER,
  ],
  // COURSE
  ADMIN_COURSE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_DELETE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_PUBLISH,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CHAT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_DELETE_WITH_CREDITS,
  ],
  COURSE: [
    USER_PERMISSION_INDIVIDUALS.COURSE_VIEW,
    USER_PERMISSION_INDIVIDUALS.COURSE_CHAT,
    USER_PERMISSION_INDIVIDUALS.COURSE_CALL,
    USER_PERMISSION_INDIVIDUALS.COURSE_CLASS_BOOKING,
    USER_PERMISSION_INDIVIDUALS.COURSE_STUDENT_NAME,
    USER_PERMISSION_INDIVIDUALS.COURSE_TUTOR_NAME,
    USER_PERMISSION_INDIVIDUALS.COURSE_TOPUP,
  ],
  // CLASS
  ADMIN_CLASS: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_CLASS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_CLASS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_CLASS_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_CLASS_DELETE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_CLASS_PUBLISH,
    USER_PERMISSION_INDIVIDUALS.ADMIN_CLASS_CHAT,
  ],
  CLASS: [
    USER_PERMISSION_INDIVIDUALS.CLASS_VIEW,
    USER_PERMISSION_INDIVIDUALS.CLASS_CHAT,
  ],
  // COURSE TEMPLATE
  ADMIN_COURSE_TEMPLATE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_DELETE,
  ],
  // SCHEDULE
  ADMIN_SCHEDULE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_EDIT,
  ],
  SCHEDULE: [
    USER_PERMISSION_INDIVIDUALS.SCHEDULE_VIEW,
    USER_PERMISSION_INDIVIDUALS.SCHEDULE_EDIT,
  ],
  // LESSON HISTORY
  ADMIN_LESSON_HISTORY: [USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_VIEW],
  LESSON_HISTORY: [USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_VIEW],
  // LESSON HISTORY TABS
  ADMIN_LESSON_HISTORY_TABS: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_RESCHEDULE_OR_CANCELLATION_TAB,
  ],
  LESSON_HISTORY_TABS: [
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_RESCHEDULE_OR_CANCELLATION_TAB,
  ],
  // LESSON HISTORY COMPLETED TAB
  LESSON_HISTORY_COMPLETED_TAB: [
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_REFERENCE_KEY,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_DATE_TIME,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_STUDENTS,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTORS,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_RECORDINGS,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_ACTIONS,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_CURRICULUM,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_SUBJECT,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_GRADE,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_VIEW_FEEDBACKS,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_LESSON_TYPE,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_PAYMENT_TYPE,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_STUDENT_JOIN_TIME,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_JOIN_TIME,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_RATE,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_AMOUNT,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_PENALTY,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_PAYMENT_BREAKDOWN,
    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TRIAL_STATUS,
  ],
  ADMIN_LESSON_HISTORY_COMPLETED_TAB: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_REFERENCE_KEY,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_DATE_TIME,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_STUDENTS,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTORS,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_RECORDINGS,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_ACTIONS,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_CURRICULUM,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_SUBJECT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_GRADE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_ATTENDANCE_HISTORY,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_ADMIN_NOTES,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_VIEW_FEEDBACKS,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_LESSON_TYPE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_TYPE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_STUDENT_COUNTRIES,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_STUDENT_JOIN_TIME,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_JOIN_TIME,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_RATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_AMOUNT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_PAYMENT_STATUS,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_PENALTY,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_ADJUSTMENTS,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_CLASS_EXTENSION,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_TUTOR_PAYMENT_HISTORY,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_TUTOR_PENALTY,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_TRIAL_CONVERSION,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PENALTY_WAIVERS,
    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_TRIAL_STATUS,
  ],
  // ROLES
  ADMIN_ROLES: [USER_PERMISSION_INDIVIDUALS.ADMIN_ROLES_VIEW],
  // BILLING
  BILLING: [
    USER_PERMISSION_INDIVIDUALS.BILLING_VIEW,
    USER_PERMISSION_INDIVIDUALS.CUSTOMER_PORTAL_VIEW,
  ],
  // TRIAL CLASS
  ADMIN_TRIAL_CLASS: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_TRIAL_CLASS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_TRIAL_CLASS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_TRIAL_CLASS_EDIT,
  ],
  TRIAL_CLASS: [
    USER_PERMISSION_INDIVIDUALS.TRIAL_CLASS_VIEW,
    USER_PERMISSION_INDIVIDUALS.TRIAL_CLASS_CREATE,
  ],
  // PROFILE
  PROFILE: [
    USER_PERMISSION_INDIVIDUALS.PROFILE_VIEW,
    USER_PERMISSION_INDIVIDUALS.PROFILE_EDIT,
    USER_PERMISSION_INDIVIDUALS.PROFILE_DELETE,
    USER_PERMISSION_INDIVIDUALS.PROFILE_QUALIFICATIONS,
    USER_PERMISSION_INDIVIDUALS.PROFILE_SUBJECTS,
    USER_PERMISSION_INDIVIDUALS.PROFILE_PARENT,
    USER_PERMISSION_INDIVIDUALS.PROFILE_PLANS,
    USER_PERMISSION_INDIVIDUALS.PROFILE_PAYMENT_HISTORY,
    USER_PERMISSION_INDIVIDUALS.PROFILE_BILLING_EMAIL,
    USER_PERMISSION_INDIVIDUALS.PROFILE_TUTOR_RATES,
    USER_PERMISSION_INDIVIDUALS.PROFILE_DATE_OF_BIRTH,
    USER_PERMISSION_INDIVIDUALS.PROFILE_NATIONALITY,
    USER_PERMISSION_INDIVIDUALS.PROFILE_EMERGENCY_NUMBER,
    USER_PERMISSION_INDIVIDUALS.PROFILE_TUTOR,
    USER_PERMISSION_INDIVIDUALS.PROFILE_BANK,
    USER_PERMISSION_INDIVIDUALS.PROFILE_CURRICULUM,
    USER_PERMISSION_INDIVIDUALS.PROFILE_EXAM_DATE,
    USER_PERMISSION_INDIVIDUALS.PROFILE_ADMIN_TAG,
    USER_PERMISSION_INDIVIDUALS.PROFILE_CLASS_STATEMENT,
    USER_PERMISSION_INDIVIDUALS.PROFILE_MS_TEAMS_EMAIL,
  ],
  ADMIN_USER_PROFILE: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_USER_PROFILE_ADMIN_TAG,
  ],
  // HOMEWORK
  ADMIN_HOMEWORK: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_HOMEWORK_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_HOMEWORK_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_HOMEWORK_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_HOMEWORK_DELETE,
  ],
  HOMEWORK: [
    USER_PERMISSION_INDIVIDUALS.HOMEWORK_VIEW,
    USER_PERMISSION_INDIVIDUALS.HOMEWORK_CREATE,
    USER_PERMISSION_INDIVIDUALS.HOMEWORK_EDIT,
    USER_PERMISSION_INDIVIDUALS.HOMEWORK_DELETE,
  ],
  // USERS
  ADMIN_USERS: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_USERS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_USERS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_USERS_EDIT,
  ],
  // SETTINGS
  ADMIN_SETTINGS: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_VIEW,
    // FLEXIBLE PLANS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_DELETE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_FLEXIBLE_PLANS_EDIT,
    // SUBSCRIPTION PLANS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_DELETE,
    // GROUPS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_GROUPS_DELETE,
    // COURSE PERMISSION TEMPLATES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PERMISSION_TEMPLATES_DELETE,
    // STUDENT RATES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_DELETE,
    // COURSE PRICING TEMPLATES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_COURSE_PRICING_TEMPLATES_DELETE,
    // TRANSACTION LOGS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TRANSACTION_LOGS_VIEW,
    // PAYMENT HISTORY
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_PAYMENT_HISTORY_VIEW,
    // INVOICES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_EDIT,
    // ADJUSTMENTS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ADJUSTMENTS_CREATE,
    // TUTOR RATES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_RATES_DELETE,
    // ROLES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ROLES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ROLES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ROLES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_ROLES_DELETE,
    // TUTOR ROLES
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_ROLES_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_ROLES_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_ROLES_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_ROLES_DELETE,
    // PERMISSIONS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_PERMISSIONS_VIEW,
    // META TAGS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_META_TAGS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_META_TAGS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_META_TAGS_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_META_TAGS_DELETE,
    // REDIRECTS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_REDIRECTIONS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_REDIRECTIONS_CREATE,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_REDIRECTIONS_EDIT,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_REDIRECTIONS_DELETE,
    // SITEMAP
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SITEMAP_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SITEMAP_CREATE,
    // TUTOR SORTING
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_SORTING_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_TUTOR_SORTING_EDIT,
    // CALL LOGS
    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_CALL_LOGS_VIEW,
  ],
  // HEADER_CONTENTS
  HEADER_CONTENTS: [
    USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_PROFILE_DROPDOWN,
    USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_SIGNUP_BUTTONS,
    USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_DISCOUNT_BANNER,
    USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_DASHBOARD,
    USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_CREDITS_VIEWER,
    USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_UPGRADE_BUTTON,
  ],
  // PENALTY WAIVERS
  PENALTY_WAIVERS: [
    USER_PERMISSION_INDIVIDUALS.PENALTY_WAIVERS_VIEW,
    USER_PERMISSION_INDIVIDUALS.PENALTY_APPEAL_REQUEST,
  ],
  ADMIN_PENALTY_WAIVERS: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_PENALTY_WAIVERS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_PENALTY_APPEAL_REQUEST,
  ],
  // MANUAL ADJUSTMENTS
  ADMIN_MANUAL_ADJUSTMENTS: [
    USER_PERMISSION_INDIVIDUALS.ADMIN_MANUAL_ADJUSTMENTS_VIEW,
    USER_PERMISSION_INDIVIDUALS.ADMIN_MANUAL_ADJUSTMENTS_CREATE,
  ],
  // QUIZ PERFORMANCE
  QUIZ_PERFORMANCE: [USER_PERMISSION_INDIVIDUALS.QUIZ_PERFORMANCE_VIEW],
  // MY PERFORMANCE
  MY_PERFORMANCE: [USER_PERMISSION_INDIVIDUALS.MY_PERFORMANCE_VIEW],
  // TUTORS
  TUTORS: [USER_PERMISSION_INDIVIDUALS.TUTORS_HIDE],
  // USER TYPES
  USER_TYPES: [
    USER_PERMISSION_INDIVIDUALS.STUDENT,
    USER_PERMISSION_INDIVIDUALS.TUTOR,
    USER_PERMISSION_INDIVIDUALS.ADMIN,
    USER_PERMISSION_INDIVIDUALS.GUEST,
  ],
  // META TAGS
  META_TAGS: [USER_PERMISSION_INDIVIDUALS.META_TAGS_VIEW],
  // RESOURCES
  RESOURCES: [
    USER_PERMISSION_INDIVIDUALS.RESOURCES_WHITEBOARD,
    USER_PERMISSION_INDIVIDUALS.RESOURCES_SESSIONS,
    USER_PERMISSION_INDIVIDUALS.RESOURCES_BOOKMARKS,
    USER_PERMISSION_INDIVIDUALS.RESOURCES_PROGRESS,
    USER_PERMISSION_INDIVIDUALS.RESOURCES_PREMIUM,
    USER_PERMISSION_INDIVIDUALS.RESOURCES_USAGE,
  ],
  // QUIZ BUILDER
  QUIZ_BUILDER: [
    USER_PERMISSION_INDIVIDUALS.QUIZ_BUILDER_VIEW,
    USER_PERMISSION_INDIVIDUALS.QUIZ_BUILDER_CREATE,
    USER_PERMISSION_INDIVIDUALS.QUIZ_BUILDER_EDIT,
    USER_PERMISSION_INDIVIDUALS.QUIZ_BUILDER_DELETE,
  ],
};

// USER
export type TUserPermissionSubmissions = typeof USER_PERMISSION['SUBMISSION'];
export type TUserPermissionCourses = typeof USER_PERMISSION['COURSE'];
export type TUserPermissionClasses = typeof USER_PERMISSION['CLASS'];
export type TUserPermissionSchedule = typeof USER_PERMISSION['SCHEDULE'];
export type TUserPermissionProfile = typeof USER_PERMISSION['PROFILE'];
export type TUserPermissionTrialClass = typeof USER_PERMISSION['TRIAL_CLASS'];
export type TUserPermissionLessonHistory = typeof USER_PERMISSION['LESSON_HISTORY'];
export type TUserPermissionLessonHistoryTabs = typeof USER_PERMISSION['LESSON_HISTORY_TABS'];
export type TUserPermissionHomework = typeof USER_PERMISSION['HOMEWORK'];
export type TUserPermissionBilling = typeof USER_PERMISSION['BILLING'];
export type TUserPermissionLessonHistoryCompletedTab = typeof USER_PERMISSION['LESSON_HISTORY_COMPLETED_TAB'];
export type TUserPermissionHeaderContents = typeof USER_PERMISSION['HEADER_CONTENTS'];
export type TUserPermissionPenaltyWaivers = typeof USER_PERMISSION['PENALTY_WAIVERS'];
export type TUserPermissionQuizPerformance = typeof USER_PERMISSION['QUIZ_PERFORMANCE'];
export type TUserPermissionMyPerformance = typeof USER_PERMISSION['MY_PERFORMANCE'];
export type TUserPermissionTutors = typeof USER_PERMISSION['TUTORS'];
export type TUserPermissionUserTypes = typeof USER_PERMISSION['USER_TYPES'];
export type TUserPermissionMetaTags = typeof USER_PERMISSION['META_TAGS'];
export type TUserPermissionResources = typeof USER_PERMISSION['RESOURCES'];
export type TUserPermissionQuizBuilder = typeof USER_PERMISSION['QUIZ_BUILDER'];
// ADMIN
export type TUserPermissionAdminCourses = typeof USER_PERMISSION['ADMIN_COURSE'];
export type TUserPermissionAdminClasses = typeof USER_PERMISSION['ADMIN_CLASS'];
export type TUserPermissionAdminCourseTemplates = typeof USER_PERMISSION['ADMIN_COURSE_TEMPLATE'];
export type TUserPermissionAdminSchedule = typeof USER_PERMISSION['ADMIN_SCHEDULE'];
export type TUserPermissionAdminTrialClass = typeof USER_PERMISSION['ADMIN_TRIAL_CLASS'];
export type TUserPermissionAdminLessonHistory = typeof USER_PERMISSION['ADMIN_LESSON_HISTORY'];
export type TUserPermissionAdminLessonHistoryTabs = typeof USER_PERMISSION['ADMIN_LESSON_HISTORY_TABS'];
export type TUserPermissionAdminHomework = typeof USER_PERMISSION['ADMIN_HOMEWORK'];
export type TUserPermissionAdminUsers = typeof USER_PERMISSION['ADMIN_USERS'];
export type TUserPermissionAdminSettings = typeof USER_PERMISSION['ADMIN_SETTINGS'];
export type TUserPermissionAdminRoles = typeof USER_PERMISSION['ADMIN_ROLES'];
export type TUserPermissionAdminLessonHistoryCompletedTab = typeof USER_PERMISSION['ADMIN_LESSON_HISTORY_COMPLETED_TAB'];
export type TUserPermissionAdminPenaltyWaivers = typeof USER_PERMISSION['ADMIN_PENALTY_WAIVERS'];
export type TUserPermissionAdminManualAdjustments = typeof USER_PERMISSION['ADMIN_MANUAL_ADJUSTMENTS'];
export type TUserPermissionAdminUserProfile = typeof USER_PERMISSION['ADMIN_USER_PROFILE'];

export interface IRolePermissions {
  quiz?: Array<'view'>;
  submission?: TUserPermissionSubmissions;
  quizHistory?: Array<'view'>;
  headerContents?: TUserPermissionHeaderContents;
  course?: TUserPermissionCourses;
  class?: TUserPermissionClasses;
  schedule?: TUserPermissionSchedule;
  profile?: TUserPermissionProfile;
  trialClass?: TUserPermissionTrialClass;
  lessonHistory?: TUserPermissionLessonHistory;
  lessonHistoryTabs?: TUserPermissionLessonHistoryTabs;
  lessonHistoryCompletedTab?: TUserPermissionLessonHistoryCompletedTab;
  homework?: TUserPermissionHomework;
  billing?: TUserPermissionBilling;
  penaltyWaivers?: TUserPermissionPenaltyWaivers;
  quizPerformance?: TUserPermissionQuizPerformance;
  myPerformance?: TUserPermissionMyPerformance;
  tutors?: TUserPermissionTutors;
  userTypes?: TUserPermissionUserTypes;
  metaTags?: TUserPermissionMetaTags;
  resources?: TUserPermissionResources;
  quizBuilder?: TUserPermissionQuizBuilder;
  // ADMIN
  adminSubTopic?: Array<'view' | 'create' | 'edit' | 'delete'>;
  adminQuestion?: Array<'view' | 'create' | 'edit' | 'delete' | 'approve'>;
  adminQuiz?: Array<'view' | 'create' | 'edit' | 'delete' | 'publish'>;
  adminStudent?: Array<'view'>;
  adminCourse?: TUserPermissionAdminCourses;
  adminClass?: TUserPermissionAdminClasses;
  adminCourseTemplate?: TUserPermissionAdminCourseTemplates;
  adminSchedule?: TUserPermissionAdminSchedule;
  adminLessonHistory?: TUserPermissionAdminLessonHistory;
  adminLessonHistoryTabs?: TUserPermissionAdminLessonHistoryTabs;
  adminLessonHistoryCompletedTab?: TUserPermissionAdminLessonHistoryCompletedTab;
  adminTrialClass?: TUserPermissionAdminTrialClass;
  adminHomework?: TUserPermissionAdminHomework;
  adminUsers?: TUserPermissionAdminUsers;
  adminSettings?: TUserPermissionAdminSettings;
  adminPenaltyWaivers?: TUserPermissionAdminPenaltyWaivers;
  adminManualAdjustments?: TUserPermissionAdminManualAdjustments;
  adminUserProfile?: TUserPermissionAdminUserProfile;
}

export interface IRoleNotifications {
  email: {
    classReminders: {
      types: string[];
      [key: string]: any;
    };
    homeworks: {
      types: string[];
      [key: string]: any;
    };
    payments: {
      types: string[];
      [key: string]: any;
    };
  };
  whatsapp: {
    classReminders: {
      types: string[];
      [key: string]: any;
    };
    homeworks: {
      types: string[];
      [key: string]: any;
    };
    payments: {
      types: string[];
      [key: string]: any;
    };
  };
}

export interface IRole {
  _id: string;
  name: TRoleNames;
  type: TRoleTypes;
  createdAt: string;
  permissions?: IRolePermissions;
  isGuest?: boolean;
  updatedAt?: string;
  notifications: IRoleNotifications;
}

export interface IRoleDeleteVerificationData {
  usersCount: number;
}

export interface IInitialRolesState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminRolesListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IRole>;
  };
  adminRolesListDataForDropdown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IRole>;
  };
  adminRoleDeleteVerificationData?: IRoleDeleteVerificationData;
}

const initialState: IInitialRolesState = {
  status: 'idle',
  // Admin
  adminRolesListData: undefined,
  adminRolesListDataForDropdown: undefined,
  adminRoleDeleteVerificationData: undefined,
};

export const RolesSlice = createSlice({
  initialState,
  name: 'rolesSlice',
  reducers: {
    resetAdminRolesListData: (state) => {
      state.adminRolesListData = undefined;
    },
    resetAdminRoleDeleteVerificationData: (state) => {
      state.adminRoleDeleteVerificationData = undefined;
    },
    resetAdminRolesListDataForDropdown: (state) => {
      state.adminRolesListDataForDropdown = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(adminGetRoles.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminRolesListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminRolesListData = newAdminRolesListData;
      })
      .addCase(adminGetRolesForDropdown.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminRolesListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminRolesListDataForDropdown = newAdminRolesListData;
      })
      .addCase(adminCreateRole.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminRolesListData) {
          const newAdminRolesListData = {
            ...state.adminRolesListData,
          };

          newAdminRolesListData.list.unshift(result);

          if (newAdminRolesListData.totalDocs) {
            newAdminRolesListData.totalDocs += 1;
          } else {
            newAdminRolesListData.totalDocs = 1;
          }

          state.adminRolesListData = newAdminRolesListData;
        }
      })
      .addCase(adminUpdateRole.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminRolesListData &&
          state.adminRolesListData.list &&
          state.adminRolesListData.list.length
        ) {
          const newAdminRolesListData = {
            ...state.adminRolesListData,
          };

          const index = newAdminRolesListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminRolesListData.list[index] = result;
          }

          state.adminRolesListData = newAdminRolesListData;
        }
      })
      .addCase(adminDeleteRole.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminRolesListData &&
          state.adminRolesListData.list &&
          state.adminRolesListData.list.length
        ) {
          const newAdminRolesListData = {
            ...state.adminRolesListData,
          };

          newAdminRolesListData.totalDocs = newAdminRolesListData.totalDocs
            ? newAdminRolesListData.totalDocs - 1
            : 0;

          newAdminRolesListData.list = newAdminRolesListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminRolesListData = newAdminRolesListData;
        }
      })
      .addCase(adminVerifyRoleDelete.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminRoleDeleteVerificationData = result;
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        adminCreateRole,
        adminGetRoles,
        adminUpdateRole,
        adminDeleteRole,
        adminVerifyRoleDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        adminCreateRole,
        adminGetRoles,
        adminUpdateRole,
        adminDeleteRole,
        adminVerifyRoleDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateRole,
        adminGetRoles,
        adminUpdateRole,
        adminDeleteRole,
        adminVerifyRoleDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getRolesState = (state: RootState): RootState['roles'] =>
  state.roles;

export const {
  resetAdminRolesListData,
  resetAdminRoleDeleteVerificationData,
  resetAdminRolesListDataForDropdown,
} = RolesSlice.actions;

export * from './adminApi';
export default RolesSlice.reducer;
